import React, { useEffect, useState } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import DashboardStats from './Manageadmins/DashboardStats';
import TransactionsManagement from './Manageadmins/Transactions'; // Renamed from AdminManagement
import AddAdminCard from './Manageadmins/AddAdmin';
import DashboardLayout from './DashboardLayout';
import baseURL from '../config';
import { toast } from 'react-toastify';
import PageLoader from '../../../Reusable/PageLoader';

const ManageAdmins = () => {
    const [adminRole, setAdminRole] = useState('');
    const [stats, setStats] = useState({});
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [processingAction, setProcessingAction] = useState(false);

    // This function can be passed to children to trigger a data refresh.
    const fetchAdmins = async () => {
        try {
        // await new Promise(resolve => setTimeout(resolve, 4000));

            const response = await fetch(`${baseURL}/admin/admins`, {
                method: 'GET',
                credentials: 'include',
            });
            if (!response.ok) {
                throw new Error('Failed to fetch admin list');
            }
            const data = await response.json();
            setAdmins(data);
        } catch (err) {
            console.error('Error fetching admins:', err);
            toast.error('Failed to reload admin data.');
        }
    };

    useEffect(() => {
        const fetchAllData = async () => {
            setLoading(true);
            try {
        // await new Promise(resolve => setTimeout(resolve, 4000));

                // Fetch all necessary data concurrently
                const [roleResponse, statsResponse, adminsResponse] = await Promise.all([
                    fetch(`${baseURL}/admin/me`, { credentials: 'include' }),
                    fetch(`${baseURL}/admin/stats`, { credentials: 'include' }),
                    fetch(`${baseURL}/admin/admins`, { credentials: 'include' }),
                ]);

                // Check for HTTP errors
                if (!roleResponse.ok || !statsResponse.ok || !adminsResponse.ok) {
                    throw new Error('Failed to fetch all required data from the server.');
                }

                // Parse JSON data from all responses
                const roleData = await roleResponse.json();
                const statsData = await statsResponse.json();
                const adminsData = await adminsResponse.json();

                // Update the state
                setAdminRole(roleData.role);
                setStats(statsData);
                setAdmins(adminsData);

            } catch (err) {
                console.error('Error fetching data:', err);
                setError(err.message || 'Failed to load dashboard data. Please try again.');
                toast.error(err.message || 'Failed to load dashboard data.');
            } finally {
                setLoading(false);
            }
        };

        fetchAllData();
    }, []);

    const isSuperAdmin = adminRole === 'super_Admin';

    if (loading) {
        return (
            <DashboardLayout>
                <PageLoader message="Loading admin data..." />
            </DashboardLayout>
        );
    }

    if (error) {
        return (
            <DashboardLayout>
                <Box sx={{ p: 4 }}>
                    <Alert severity="error">{error}</Alert>
                </Box>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <Typography
                variant="h6"
                sx={{
                    color: '#666',
                    marginBottom: 2,
                    textAlign: 'left',
                    marginTop: 2,
                    fontWeight: '600',
                }}
            >
                Manage Admins
            </Typography>

            {isSuperAdmin ? (
                <>
                    {/* Pass the fetched stats as a prop */}
                    <DashboardStats stats={stats} />
                    {/* Pass the fetched admins and the refresh function as props */}
                    <TransactionsManagement
                        admins={admins}
                        fetchAdmins={fetchAdmins}
                        processingAction={processingAction}
                        setProcessingAction={setProcessingAction}
                    />
                    {/* Pass the refresh function to the AddAdminCard */}
                    <AddAdminCard fetchAdmins={fetchAdmins} />
                </>
            ) : (
                <Alert severity="warning" sx={{ mt: 4 }}>
                    You have limited access to this page. Only super admins can manage this section.
                </Alert>
            )}
        </DashboardLayout>
    );
};

export default ManageAdmins;